import React from "react";
import { Helmet } from "react-helmet-async";
import { SEO_NICHE_KEYWORDS } from "../../config/seoKeywords";
import { Link } from "react-router-dom";
import { useStateContext } from '../../components/contexts/ContextProvider';
import {
    Smartphone,
    Ticket,
    Search,
    Bell,
    QrCode,
    Percent,
    CreditCard,
    Users,
    BarChart3,
    ScanLine,
    Megaphone,
    Sparkles,
    ArrowRight,
} from "lucide-react";

const STORES = {
    apple: "https://apps.apple.com/ci/app/events-billetterie/id6738791983",
    google: "https://play.google.com/store/apps/details?id=com.event.event_app",
};

const ACHETEURS_ATOUTS = [
    {
        icon: Search,
        title: "Découvrir et réserver",
        text: "Parcourez les événements près de chez vous, filtrez par date ou catégorie et réservez en quelques gestes.",
    },
    {
        icon: CreditCard,
        title: "Paiement sécurisé",
        text: "Réglez vos billets en ligne avec un parcours fluide et des moyens de paiement adaptés au contexte local.",
    },
    {
        icon: Ticket,
        title: "Billets toujours avec vous",
        text: "Retrouvez vos achats, vos confirmations et vos QR codes dans l’app — plus besoin d’imprimer au dernier moment.",
    },
    {
        icon: Bell,
        title: "Restez informé",
        text: "Recevez les rappels et informations utiles pour ne rien manquer de tes événements.",
    },
    {
        icon: QrCode,
        title: "Entrée simplifiée",
        text: "Présentez votre billet numérique à l’entrée : contrôle rapide et file d’attente réduite.",
    },
    {
        icon: Percent,
        title: "Réductions et offres spéciales",
        text: "Profitez régulièrement de codes promo, offres de groupe directement via l'app lors de l'achat de vos billets.",
    },
];

const ORGANISATEURS_ATOUTS = [
    {
        icon: Megaphone,
        title: "Visibilité mobile",
        text: "Vos événements sont exposés là où le public passe le plus de temps : sur le téléphone.",
    },
    {
        icon: BarChart3,
        title: "Pilotage des ventes",
        text: "Suivez l’activité, les volumes et l’évolution de vos ventes sans attendre un retour bureau.",
    },
    {
        icon: Users,
        title: "Relation avec le public",
        text: "Offrez un canal simple à vos acheteurs : découverte, achat et reprise de contact depuis une seule app.",
    },
    {
        icon: ScanLine,
        title: "Cohérence avec l’entrée",
        text: "Les billets émis restent alignés avec vos contrôles d’accès et la validation sur le terrain.",
    },
    {
        icon: Sparkles,
        title: "Image professionnelle",
        text: "Une app dédiée renforce la crédibilité de votre marque et rassure partenaires et sponsors.",
    },
    {
        icon: Smartphone,
        title: "Le même écosystème",
        text: "Une expérience unifiée entre le web et le mobile pour vous et pour vos participants.",
    },
];

export default function MobileApp() {
    const imagePath = process.env.REACT_APP_IMAGE_PATH;
    const { user } = useStateContext();
    const canonicalUrl = "https://eventsbilletterie.com/application-mobile";
    const seoTitle = "Application mobile billetterie evenementielle | Events billetterie";
    const seoDescription =
        "Application mobile de billetterie evenementielle en Cote d'Ivoire : achat de billets, QR code, notifications et suivi des ventes pour organisateurs.";
    const seoKeywords =
        `application billetterie, achat billet en ligne, billet mobile, ${SEO_NICHE_KEYWORDS}`;
    const seoImage = "https://eventsbilletterie.com/assets/images/facadeEvents.png";
    const mobileAppSeoJsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: seoTitle,
        description: seoDescription,
        url: canonicalUrl,
        inLanguage: "fr-CI",
        about: [
            "Application mobile billetterie",
            "Achat de billets evenementiels",
            "QR code et controle d'entree",
            "Gestion evenementielle mobile",
        ],
        isPartOf: {
            "@type": "WebSite",
            name: "Events Billetterie",
            url: "https://eventsbilletterie.com",
        },
        publisher: {
            "@type": "Organization",
            name: "Events Billetterie",
            url: "https://eventsbilletterie.com",
        },
    };


    return (
        <main className="nc-PageAbout relative overflow-hidden">
            <Helmet>
                <html lang="fr" />
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_CI" />
                <meta property="og:site_name" content="Events Billetterie" />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(mobileAppSeoJsonLd)}
                </script>
            </Helmet>

            <div className="nc-BgGlassmorphism pointer-events-none absolute inset-x-0 top-0 min-h-[28rem] pl-20 py-24 flex overflow-hidden z-0">
                <span className="block bg-[#04868b] w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-[0.12] lg:w-96 lg:h-96" />
                <span className="block bg-primary-6000 w-72 h-72 -ml-20 mt-32 rounded-full mix-blend-multiply filter blur-3xl opacity-[0.1] lg:w-96 lg:h-96" />
            </div>

            <div className="container relative z-[1] px-4 pt-12 pb-20 md:pt-16 md:pb-28">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary-6000/10 text-primary-700 dark:text-primary-400 px-4 py-1.5 text-sm font-medium mb-6">
                        <Smartphone className="w-4 h-4" aria-hidden />
                        Application mobile
                    </div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-neutral-50 tracking-tight">
                    <span className="text-primary-6000">Events Billetterie</span> pour vos publics{" "}
                        et vos équipes
                    </h1>
                    <p className="mt-5 text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        Une même application pour découvrir des événements, acheter des billets en toute simplicité et
                        accompagner les organisateurs qui font vivre la billetterie sur le terrain.
                    </p>
                </div>

                <div className="mt-12 lg:mt-16 max-w-5xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                    <div className="relative rounded-3xl overflow-hidden shadow-xl">
                        <img
                            src={`${imagePath}images/facadeEvents.png`}
                            alt="Aperçu de l'application Events sur smartphone"
                            className="w-full h-auto object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <div className="flex flex-col justify-center space-y-6">
                        <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                            Téléchargez <strong className="text-neutral-800 dark:text-neutral-200">Events Billetterie</strong>{" "}
                            sur iOS ou Android pour un parcours fluide, des notifications utiles et des billets
                            numériques toujours accessibles — que vous soyez spectateur ou organisateur.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href={STORES.apple}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-100 hover:border-primary-6000/50 transition-colors"
                            >
                                <img
                                    src={`${imagePath}images/apple-logo.png`}
                                    alt=""
                                    className="w-7 h-7 object-contain"
                                    loading="lazy"
                                />
                                App Store
                            </a>
                            <a
                                href={STORES.google}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-100 hover:border-primary-6000/50 transition-colors"
                            >
                                <img
                                    src={`${imagePath}images/android%20logo.png`}
                                    alt=""
                                    className="w-7 h-7 object-contain"
                                    loading="lazy"
                                />
                                Google Play
                            </a>
                        </div>
                    </div>
                </div>

                <section className="mt-24 md:mt-32 max-w-6xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                            On te recommande d'installer l'application
                        </h2>
                        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
                            Tout ce qu’il faut pour réserver, payer et profiter de l’événement et une expérience utilisateur fluide sans friction.
                        </p>
                    </div>
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 list-none p-0 m-0">
                        {ACHETEURS_ATOUTS.map(({ icon: Icon, title, text }) => (
                            <li
                                key={title}
                                className="rounded-2xl border border-neutral-200/90 dark:border-neutral-700 bg-white/80 dark:bg-neutral-900/50 p-6 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-6000/10 text-primary-6000 mb-4">
                                    <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                                </div>
                                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
                                <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{text}</p>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 flex justify-center gap-4 items-center">
                        <Link
                            to="/go"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg bg-primary-6000 hover:bg-primary-6000/80 text-white font-semibold px-6 py-3 text-sm transition-colors"
                        >
                            Télécharger gratuitement
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </Link>
                        <Link
                            to="/events"
                            className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-6000 underline-offset-4 hover:underline"
                        >
                            Découvrir les événements
                        </Link>
                    </div>
                </section>

                <section className="mt-24 md:mt-32 max-w-6xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                            Pour les organisateurs
                        </h2>
                        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
                            Renforcez votre billetterie avec un canal mobile aligné sur vos objectifs de vente et
                            d’image.
                        </p>
                    </div>
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 list-none p-0 m-0">
                        {ORGANISATEURS_ATOUTS.map(({ icon: Icon, title, text }) => (
                            <li
                                key={title}
                                className="rounded-2xl border border-neutral-200/90 dark:border-neutral-700 bg-neutral-50/90 dark:bg-neutral-800/40 p-6 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-600/10 text-teal-700 dark:text-teal-400 mb-4">
                                    <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                                </div>
                                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
                                <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{text}</p>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        {user ? (
                            <Link
                                to="/dashboard/events/registration"
                                className="inline-flex items-center gap-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-semibold px-6 py-3 text-sm transition-colors"
                            >
                                Ouvrir ma billetterie
                                <ArrowRight className="w-4 h-4" aria-hidden />
                            </Link>
                        ) : (
                            <Link
                                to="/create-account"
                                className="inline-flex items-center gap-2 rounded-lg border-2 border-primary-6000 text-primary-6000 dark:text-primary-400 font-semibold px-6 py-3 text-sm hover:bg-primary-6000/10 transition-colors"
                            >
                                Devenir organisateur
                                <ArrowRight className="w-4 h-4" aria-hidden />
                            </Link>
                        )}
                        <Link
                            to="/offre-billetterie"
                            className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-6000 underline-offset-4 hover:underline"
                        >
                            Découvrir la billetterie Events
                        </Link>
                    </div>
                </section>
            </div>
        </main>
    );
}
